import React from "react";
import { Box, Input, SimpleGrid, Spinner, Stack } from "@chakra-ui/react";
import { LuUserRound } from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";
import { usePerfilSettings } from "features/perfil/settings/store";
import { useUpdateMiFamilia } from "features/perfil/familia/hooks";
import FamiliaEditorLayout, { EditorField, editorInputProps } from "./FamiliaEditorLayout";
import RedirectStatusPage from "../../main/Status/RedirectStatusPage";

const EMPTY = { nombre: "", apellidoPaterno: "", apellidoMaterno: "", telefono: "", correo: "" };
const digits = (value) => String(value || "").replace(/\D/g, "").slice(0, 10);
const formatPhone = (value) => {
  const phone = digits(value);
  if (phone.length <= 2) return phone;
  if (phone.length <= 6) return `${phone.slice(0, 2)} ${phone.slice(2)}`;
  return `${phone.slice(0, 2)} ${phone.slice(2, 6)} ${phone.slice(6)}`;
};
const validEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

function formFromFamily(type, family) {
  if (!family) return EMPTY;
  const mother = type === "madre";
  return {
    nombre: (mother ? family.nombreMama : family.nombrePapa) || "",
    apellidoPaterno: (mother ? family.apellidoPaternoMama : family.apellidoPaternoPapa) || "",
    apellidoMaterno: (mother ? family.apellidoMaternoMama : family.apellidoMaternoPapa) || "",
    telefono: formatPhone((mother ? family.telefonoMama : family.telefonoPapa) || ""),
    correo: (mother ? family.correoMama : family.correoPapa) || "",
  };
}

export default function EditarContactoPerfil() {
  const { tipo } = useParams();
  const navigate = useNavigate();
  const perfil = usePerfilSettings();
  const updateFamily = useUpdateMiFamilia();
  const [form, setForm] = React.useState(EMPTY);
  const initialized = React.useRef(false);
  const validType = tipo === "madre" || tipo === "padre";

  React.useEffect(() => {
    if (!perfil.familia || initialized.current || !validType) return;
    setForm(formFromFamily(tipo, perfil.familia));
    initialized.current = true;
  }, [perfil.familia, tipo, validType]);

  if (!validType) return <RedirectStatusPage to="/perfil/familia" eyebrow="Contacto no encontrado" title="Ese contacto no está disponible" description="Volveremos al resumen para que puedas elegir a mamá o papá." />;
  if (!perfil.ready) return <Box minH="100dvh" display="grid" placeItems="center"><Spinner color="brand.red"/></Box>;

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const valid = form.nombre.trim().length >= 2 && form.apellidoPaterno.trim() && form.apellidoMaterno.trim() && digits(form.telefono).length === 10 && validEmail(form.correo);
  const save = async () => {
    const mother = tipo === "madre";
    await updateFamily.mutateAsync(mother ? {
      nombreMama: form.nombre.trim(), apellidoPaternoMama: form.apellidoPaterno.trim(), apellidoMaternoMama: form.apellidoMaterno.trim(), telefonoMama: form.telefono.trim(), correoMama: form.correo.trim().toLowerCase(),
    } : {
      nombrePapa: form.nombre.trim(), apellidoPaternoPapa: form.apellidoPaterno.trim(), apellidoMaternoPapa: form.apellidoMaterno.trim(), telefonoPapa: form.telefono.trim(), correoPapa: form.correo.trim().toLowerCase(),
    });
    await perfil.refetchContext();
    navigate("/perfil/familia", { replace: true });
  };

  const label = tipo === "madre" ? "mamá" : "papá";
  return <FamiliaEditorLayout title={`Editar a ${label}`} description="Contacto familiar" icon={<LuUserRound/>} iconBg={tipo === "madre" ? "purple.50" : "teal.50"} iconColor={tipo === "madre" ? "purple.600" : "teal.600"} onSave={save} disabled={!valid} busy={updateFamily.isPending}>
    <Stack gap="5" maxW="540px">
      <EditorField label="Nombre(s)"><Input {...editorInputProps} autoComplete="given-name" value={form.nombre} onChange={(event) => update("nombre", event.target.value)}/></EditorField>
      <SimpleGrid columns={2} gap="3">
        <EditorField label="Apellido paterno"><Input {...editorInputProps} autoComplete="family-name" value={form.apellidoPaterno} onChange={(event) => update("apellidoPaterno", event.target.value)}/></EditorField>
        <EditorField label="Apellido materno"><Input {...editorInputProps} value={form.apellidoMaterno} onChange={(event) => update("apellidoMaterno", event.target.value)}/></EditorField>
      </SimpleGrid>
      <EditorField label={"Teléfono"} hint={!form.telefono ? "10 d\u00edgitos, sin c\u00f3digo de pa\u00eds" : undefined} error={form.telefono && digits(form.telefono).length < 10 ? "Completa los 10 d\u00edgitos" : undefined}><Input {...editorInputProps} type="tel" inputMode="numeric" autoComplete="tel-national" maxLength={12} placeholder="55 1234 5678" value={form.telefono} onChange={(event) => update("telefono", formatPhone(event.target.value))}/></EditorField>
      <EditorField label={"Correo electrónico"} error={form.correo && !validEmail(form.correo) ? "Escribe un correo válido" : undefined}><Input {...editorInputProps} type="email" inputMode="email" autoComplete="email" value={form.correo} onChange={(event) => update("correo", event.target.value)}/></EditorField>
    </Stack>
  </FamiliaEditorLayout>;
}
